import React from 'react';
import {QueryGraphAction, QueryGraphEffect} from './interface';
import {isAction} from './determine-graph-action';

export function useActionHistory() {
    const [actionHistory, setActionHistory] = React.useState<
        Array<QueryGraphAction | QueryGraphEffect>
    >([]);
    const [pointerIndex, setPointerIndex] = React.useState(-1);

    const historyMoveBack = React.useCallback(() => {
        setPointerIndex((i) => i - 1);
    }, []);

    const historyMoveForward = React.useCallback(() => {
        setPointerIndex((i) => i + 1);
    }, []);

    const undoQueue = React.useMemo(() => {
        if (pointerIndex === -1) {
            return [];
        }

        let cursor = pointerIndex;
        const result: Array<QueryGraphAction | QueryGraphEffect> = [];

        while (!isAction(result[result.length - 1])) {
            if (cursor < 0) {
                return [];
            }

            result.push(actionHistory[cursor]);
            cursor--;
        }

        return result;
    }, [pointerIndex, actionHistory]);

    const redoQueue = React.useMemo(() => {
        if (pointerIndex === actionHistory.length - 1) {
            return [];
        }

        let cursor = pointerIndex + 1;
        const result: Array<QueryGraphAction | QueryGraphEffect> = [];

        do {
            result.push(actionHistory[cursor]);
            cursor++;
        } while (!isAction(actionHistory[cursor]) && cursor < actionHistory.length);

        return result;
    }, [pointerIndex, actionHistory]);

    const addToHistory = React.useCallback(
        (actionsOrEffects: Array<QueryGraphAction | QueryGraphEffect>) => {
            // we assume that effects can be added only as a result of user action, and if an effect
            // was added while there are some reverted actions that means that there was a mistake &
            // such effect can be ignored (bc if there was an action prior to this effect it would
            // clear the redoQueue). this is a hack that allows us to ignore SELECTION_CHANGED
            // effects that fire in onNodesLoaded when undoing other actions
            // (see 'selection persists when undoing' test)
            if (redoQueue.length && !isAction(actionsOrEffects[0])) {
                return;
            }

            setActionHistory((prev) => [
                ...[...prev].slice(0, pointerIndex + actionsOrEffects.length),
                ...actionsOrEffects
            ]);
            setPointerIndex(pointerIndex + actionsOrEffects.length);
        },
        [pointerIndex, redoQueue]
    );

    const clearHistory = React.useCallback(() => {
        setActionHistory([]);
        setPointerIndex(-1);
    }, []);

    return {
        actionHistory,
        addToHistory,
        historyMoveBack,
        historyMoveForward,
        undoQueue,
        redoQueue,
        clearHistory,
        pointerIndex
    };
}
